import config from '../../config'
import translate from '../../i18n'
import { ICorporateLocation, INearbyStudio } from '../../pages/corporates-container/share-type'
import { loadTencentMap } from './tencent-map-loader'
import styles from './tencent-map.module.less'
import { Alert, Spin } from 'antd'
import { useEffect, useRef, useState } from 'react'

interface TencentMapProps {
  location: Pick<ICorporateLocation, 'latitude' | 'longitude'>
  studios: Pick<INearbyStudio, 'id' | 'latitude' | 'longitude'>[]
  distanceKm: number
  selectedStudioId?: number
  onStudioSelect: (studioId: number) => void
}

const markerSvg = (color: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44"><path fill="${color}" stroke="#fff" stroke-width="2" d="M18 1C8.6 1 1 8.6 1 18c0 12.8 17 25 17 25s17-12.2 17-25C35 8.6 27.4 1 18 1z"/><circle cx="18" cy="18" r="6" fill="#fff"/></svg>`,
  )}`

const getStudioGeometries = (
  TMap: any,
  studios: Pick<INearbyStudio, 'id' | 'latitude' | 'longitude'>[],
  selectedStudioId?: number,
) =>
  studios.map(studio => ({
    id: String(studio.id),
    styleId: studio.id === selectedStudioId ? 'selected' : 'studio',
    position: new TMap.LatLng(studio.latitude, studio.longitude),
  }))

const TencentMap = ({
  location,
  studios,
  distanceKm,
  selectedStudioId,
  onStudioSelect,
}: TencentMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const tMapRef = useRef<any>()
  const studioMarkersRef = useRef<any>()
  const selectedStudioIdRef = useRef(selectedStudioId)
  const onStudioSelectRef = useRef(onStudioSelect)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()

  selectedStudioIdRef.current = selectedStudioId
  onStudioSelectRef.current = onStudioSelect

  useEffect(() => {
    if (!config.tencentMapKey) {
      setError(translate('corporate.nearbyStudios.mapKeyMissing'))
      setLoading(false)
      return
    }

    let disposed = false
    let map: any
    let studioMarkers: any
    let corporateMarker: any
    let circle: any
    setLoading(true)
    setError(undefined)

    loadTencentMap(config.tencentMapKey)
      .then(TMap => {
        if (disposed || !containerRef.current) {
          return
        }
        const center = new TMap.LatLng(location.latitude, location.longitude)
        map = new TMap.Map(containerRef.current, { center, zoom: 13 })
        tMapRef.current = TMap
        studioMarkers = new TMap.MultiMarker({
          map,
          zIndex: 100,
          styles: {
            studio: new TMap.MarkerStyle({
              width: 30,
              height: 37,
              anchor: { x: 15, y: 37 },
              src: markerSvg('#52c41a'),
            }),
            selected: new TMap.MarkerStyle({
              width: 36,
              height: 44,
              anchor: { x: 18, y: 44 },
              src: markerSvg('#fa8c16'),
            }),
          },
          geometries: getStudioGeometries(TMap, studios, selectedStudioIdRef.current),
        })
        studioMarkersRef.current = studioMarkers
        studioMarkers.on('click', (event: any) => {
          const studioId = Number(event.geometry?.id)
          if (Number.isFinite(studioId)) {
            onStudioSelectRef.current(studioId)
          }
        })

        circle = new TMap.MultiCircle({
          map,
          zIndex: 10,
          styles: {
            range: new TMap.CircleStyle({
              color: 'rgba(22,119,255,0.12)',
              showBorder: true,
              borderColor: '#1677ff',
              borderWidth: 2,
            }),
          },
          geometries: [
            {
              id: 'distance-range',
              styleId: 'range',
              center,
              radius: distanceKm * 1000,
            },
          ],
        })

        corporateMarker = new TMap.MultiMarker({
          map,
          zIndex: 1000,
          styles: {
            corporate: new TMap.MarkerStyle({
              width: 40,
              height: 49,
              anchor: { x: 20, y: 49 },
              src: markerSvg('#1677ff'),
            }),
          },
          geometries: [{ id: 'corporate-location', styleId: 'corporate', position: center }],
        })
        map.setCenter(center)
        setLoading(false)
      })
      .catch(() => {
        if (!disposed) {
          setError(translate('corporate.nearbyStudios.mapLoadFailed'))
          setLoading(false)
        }
      })

    return () => {
      disposed = true
      studioMarkers?.setMap(null)
      corporateMarker?.setMap(null)
      circle?.setMap(null)
      map?.destroy()
      studioMarkersRef.current = undefined
      tMapRef.current = undefined
    }
  }, [distanceKm, location, studios])

  useEffect(() => {
    if (studioMarkersRef.current && tMapRef.current) {
      studioMarkersRef.current.setGeometries(
        getStudioGeometries(tMapRef.current, studios, selectedStudioId),
      )
    }
  }, [selectedStudioId, studios])

  if (error) {
    return <Alert type="error" showIcon message={error} />
  }

  return (
    <div className={styles.wrapper}>
      <div ref={containerRef} className={styles.map} />
      {loading && <Spin className={styles.loading} />}
    </div>
  )
}

export default TencentMap
